import { useQuery } from '@tanstack/react-query'
import { useLocalStorage } from '@uidotdev/usehooks'
import { Col, DatePicker, Popover, Row, Tag, Tooltip, Typography } from 'antd'
import localeVi from 'antd/lib/date-picker/locale/vi_VN'
import dayjs from 'dayjs'
import { goodsQueries } from 'features/Category/screens/GoodsScreen/queries'
import { useFormik } from 'formik'
import CustomAntButton from 'general/components/Button/CustomAntButton'
import CustomAntSelect from 'general/components/CustomAntSelect'
import {
  FilterContentWrapper,
  FilterFooter,
  FilterTitleWrapper,
} from 'general/components/CustomPopover/styles'
import CustomAntInput from 'general/components/Forms/CustomAntInput/CustomAntInput'
import { PERIOD } from 'general/constants/AppConstants'
import SelectOptions from 'general/constants/SelectOptions'
import { transformToStartAndEnd } from 'general/helpers/DateHelper'
import Utils from 'general/utils/Utils'
import ValidationSchema from 'general/utils/ValidationSchema'
import useFilter from 'hooks/useFilter'
import useKeyboardPress from 'hooks/useKeyboardPress'
import useToken from 'hooks/useToken'
import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { transactionSelect } from '../constant'
import { transactionQueries } from '../queries'
import { useAppSelector } from 'hooks/useRedux'
import { shallowEqual } from 'react-redux'

export default function CustomFilter() {
  const { t } = useTranslation()
  const [filterParams, setFilterParams] = useFilter(
    'listTransactions',
    'gFilterListTransactions',
  )
  const { token } = useToken()
  const [isOpen, setIsOpen] = useState(false)
  const [savedItems] = useLocalStorage('gFilterListTransactions', null)
  const [initialValues] = useState(() => {
    let result = savedItems ?? filterParams
    result = _.omit(result, 'q')
    if (result.period === PERIOD.CUSTOM) {
      return {
        ...result,
        startDate: result.startDate
          ? dayjs(result.startDate).startOf('d')
          : transformToStartAndEnd('THIS_MONTH')[0],
        endDate: result.endDate
          ? dayjs(result.endDate).endOf('d')
          : transformToStartAndEnd('THIS_MONTH')[1],
      }
    }
    const period = result.period ?? PERIOD.THIS_MONTH
    const dateRange = transformToStartAndEnd(period)
    return {
      ...result,
      startDate: dateRange[0],
      endDate: dateRange[1],
      period: period,
    }
  })

  const { submitForm, ...formik } = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      setFilterParams({
        ...values,
        startDate: values.startDate?.format(),
        endDate: values.endDate?.format(),
        page: 1,
      })
      setClonedValues(values)
      setIsOpen(false)
    },
    validationSchema: ValidationSchema.transactionFilterSchema,
  })

  const [clonedValues, setClonedValues] = useState(formik.values) // lưu giá trị thay đôi trước khi lưu

  const { data: goodsOptions } = useQuery(
    goodsQueries.dataLogGoods({
      limit: 50,
      page: 0,
    }),
  )

  const uniqueGoodsOptions = useMemo(() => {
    if (!goodsOptions) return []

    const seen = new Set()

    return goodsOptions.filter((item) => {
      const key = `${item.label}__${item.value}`

      if (seen.has(key)) return false

      seen.add(key)
      return true
    })
  }, [goodsOptions])

  const { data: stations } = useQuery(
    transactionQueries.stationList(_.pick(filterParams, ['partnerKey'])),
  )

  const { data: nozzles, isFetching: isGettingNozzle } = useQuery(
    transactionQueries.nozzleList(
      _.isNil(formik.values.stationCode)
        ? (stations?.flatMap((v) => (v.value ? [v.stationPetroId] : [])) ?? [])
        : _.find(stations, { value: formik.values.stationCode })
            ?.stationPetroId,
    ),
  )

  const handleTogglePopover = () => {
    setIsOpen((prev) => !prev)
    //Cancel thay đổi khi đóng popover
    formik.resetForm({
      values: clonedValues,
    })
  }

  const resetDefault = () => {
    formik.resetForm()
  }

  const renderTag = (alias, tagName, label, value) => {
    if (_.isNil(value)) return null
    if (_.every(value, _.isNil)) return null
    const handleCloseTag = (_tagName, tagValue) => {
      formik.setFieldValue(_tagName, tagValue)
      submitForm()
    }

    return (
      <Tag
        prefixCls="summary-filter"
        closable
        onClose={() => {
          if (tagName === 'stationCode') {
            formik.setFieldValue('nozzleCode', null)
          }
          if (_.isArray(tagName)) {
            _.forEach(tagName, (tag) => {
              handleCloseTag(tag, initialValues[tag])
            })
          } else handleCloseTag(tagName, initialValues[tagName])
        }}
        bordered={false}
        closeIcon={<i className="fa-solid fa-times text-danger fa-lg ml-2"></i>}
        className="badge badge-light text-info w-100 d-inline-flex align-items-baseline"
      >
        <Typography.Text strong type="secondary" className="fs-6 mr-1">
          {_.upperFirst(alias)}:
        </Typography.Text>
        <Typography.Text
          style={{
            color: 'inherit',
            fontSize: '12px',
            maxWidth: '12rem',
          }}
          ellipsis={{
            tooltip: {
              destroyTooltipOnHide: true,
              title: label,
              placement: 'topRight',
            },
          }}
        >
          {label}
        </Typography.Text>
      </Tag>
    )
  }

  useKeyboardPress('escape', () => setIsOpen(false))

  useEffect(() => {
    submitForm()
  }, [submitForm])

  const systemType = useAppSelector((s) => s.auth.activeInvoice, shallowEqual)

  return (
    <div
      className="d-flex align-items-center gap-3 w-100"
      id="summary-filter-container"
    >
      <Popover
        trigger="click"
        placement="bottom"
        open={isOpen}
        onOpenChange={handleTogglePopover}
        getPopupContainer={(trigger) => trigger.parentElement}
        destroyTooltipOnHide
        arrow={false}
        overlayInnerStyle={{
          width: '50rem',
          borderRadius: token.borderRadiusLG,
        }}
        content={
          <section>
            <FilterTitleWrapper>
              <Typography.Title
                level={4}
                style={{ fontWeight: 600, marginBottom: 0 }}
              >
                {systemType?.ticket ? 'Lọc vé' : 'Lọc hóa đơn'}
              </Typography.Title>
              <CustomAntButton
                iconCn="far fa-times"
                antProps={{
                  type: 'text',
                  onClick: handleTogglePopover,
                }}
                customStyle={{
                  minWidth: 'unset',
                  borderRadius: '99999px',
                }}
              />
            </FilterTitleWrapper>
            <FilterContentWrapper onSubmit={formik.handleSubmit}>
              <Row gutter={[13, 13]} align="middle">
                <Col span={10}>
                  <Typography.Text strong>{t('Period')}</Typography.Text>
                  <CustomAntSelect
                    notFoundContent="Không tìm thấy kỳ cần tìm"
                    placeholder="Chọn hoặc tìm kiếm"
                    options={SelectOptions.periodFilterOptions}
                    value={formik.values.period}
                    onChange={(value) => {
                      const dateRange = transformToStartAndEnd(value)
                      formik.setFieldValue('period', value)
                      if (_.isEmpty(dateRange)) return
                      formik.setFieldValue('startDate', dateRange[0])
                      formik.setFieldValue('endDate', dateRange[1])
                    }}
                  />
                </Col>
                {_.range(0, 2).map((idx) => (
                  <Col span={7} key={idx}>
                    <Typography.Text strong>
                      {idx === 0 ? t('FromDate') : t('ToDate')}
                    </Typography.Text>
                    <DatePicker
                      className="w-100"
                      placement="bottomLeft"
                      allowClear={false}
                      changeOnBlur
                      name={idx === 0 ? 'startDate' : 'endDate'}
                      value={formik.values[idx === 0 ? 'startDate' : 'endDate']}
                      onChange={(date) => {
                        formik.setFieldValue(
                          idx === 0 ? 'startDate' : 'endDate',
                          date,
                        )
                        formik.setFieldValue('period', 'CUSTOM')
                      }}
                      locale={localeVi}
                      showTime
                      format={'L HH:mm'}
                      suffixIcon={<i className="fa-light fa-calendar"></i>}
                      nextIcon={<i className="fa-regular fa-angle-right"></i>}
                      prevIcon={<i className="fa-regular fa-angle-left"></i>}
                      superNextIcon={
                        <i className="fa-regular fa-angle-double-right"></i>
                      }
                      superPrevIcon={
                        <i className="fa-regular fa-angle-double-left"></i>
                      }
                    />
                  </Col>
                ))}
              </Row>
              <Row gutter={[13, 13]} align="middle">
                {/* Trạm */}
                <Col span={12} className={'mx-0'}>
                  <Typography.Text strong className="mr-2">
                    {t('listTransaction.station')}
                  </Typography.Text>
                  <CustomAntSelect
                    placeholder="Chọn hoặc tìm kiếm trạm"
                    allowClear
                    options={stations}
                    value={formik.values.stationCode}
                    onChange={(value) => {
                      formik.setFieldValue('stationCode', value)
                      formik.setFieldValue('nozzleCode', null)
                    }}
                  />
                </Col>
                {/* Cột bơm */}
                <Col span={12} className={'mx-0'}>
                  <Typography.Text strong className="mr-2">
                    {t('listTransaction.nozzle')}
                  </Typography.Text>
                  <CustomAntSelect
                    placeholder="Chọn hoặc tìm kiếm cột bơm"
                    allowClear
                    options={nozzles}
                    value={formik.values.nozzleCode}
                    onChange={(value) => {
                      formik.setFieldValue('nozzleCode', value)
                    }}
                    isLoading={isGettingNozzle}
                  />
                </Col>
                {/* Thành tiền */}
                <Col span={12} className="mx-0">
                  <Typography.Text strong className="mr-2">
                    {t('listTransaction.totalAmountStart')}
                  </Typography.Text>
                  <CustomAntInput
                    type="number"
                    borderRadius="sm"
                    inputProps={{
                      placeholder: 'Min: 1',
                      min: 1,
                      max: 999999999,
                      className: 'w-100',
                      value: formik.values.totalAmountStart,
                      onChange: (inputVal) =>
                        formik.setFieldValue(
                          'totalAmountStart',
                          _.isNumber(inputVal) ? inputVal : null,
                        ),
                      style: {
                        padding: 0,
                      },
                      status:
                        formik.errors.totalAmountStart &&
                        formik.touched.totalAmountStart
                          ? 'error'
                          : undefined,
                    }}
                  />
                </Col>
                <Col span={12} className="mx-0">
                  <Typography.Text strong className="mr-2">
                    {t('listTransaction.totalAmountEnd')}
                  </Typography.Text>
                  <CustomAntInput
                    type="number"
                    borderRadius="sm"
                    inputProps={{
                      placeholder: 'Max: 999999999',
                      min: 1,
                      max: 999999999,
                      className: 'w-100',
                      value: formik.values.totalAmountEnd,
                      onChange: (inputVal) =>
                        formik.setFieldValue(
                          'totalAmountEnd',
                          _.isNumber(inputVal) ? inputVal : null,
                        ),
                      style: {
                        padding: 0,
                      },
                      status:
                        formik.errors.totalAmountEnd &&
                        formik.touched.totalAmountEnd
                          ? 'error'
                          : undefined,
                    }}
                  />
                </Col>
                {/* Trạng thái hóa đơn */}
                <Col span={12} className="mx-0">
                  <Typography.Text strong className="mr-2">
                    {t('listTransaction.status')}
                  </Typography.Text>
                  <CustomAntSelect
                    placeholder="Chọn trạng thái hóa đơn"
                    options={transactionSelect}
                    value={formik.values.status}
                    onChange={(value) => {
                      formik.setFieldValue('status', value)
                    }}
                    listHeight={150}
                  />
                </Col>
                {/* Tên hàng hóa */}
                <Col span={12} className="mx-0">
                  <Typography.Text strong className="mr-2">
                    Tên hàng hóa
                  </Typography.Text>
                  <CustomAntSelect
                    placeholder="Chọn hoặc tìm kiếm hàng hóa"
                    options={uniqueGoodsOptions}
                    value={formik.values.productCode}
                    onChange={(value) => {
                      formik.setFieldValue('productCode', value)
                    }}
                    listHeight={150}
                  />
                </Col>
              </Row>
            </FilterContentWrapper>
            <FilterFooter
              style={{
                backgroundColor: token.colorBgBase,
              }}
            >
              <CustomAntButton
                antProps={{
                  type: 'text',
                  className: 'text-primary',
                  onClick: resetDefault,
                }}
                text={t('ResetFilter')}
              />
              <CustomAntButton
                antProps={{
                  type: 'primary',
                  onClick: submitForm,
                }}
                text={t('ApplyFilter')}
              />
            </FilterFooter>
          </section>
        }
      >
        <CustomAntButton
          text="Lọc"
          iconCn={Utils.cn('fa-duotone fa-filter-list fa-lg')}
          antProps={{
            type: 'default',
            onClick: handleTogglePopover,
          }}
          customStyle={{
            boxShadow: 'none',
            gap: '5px',
            minWidth: 'fit-content',
            color: isOpen && token.colorPrimary,
            borderColor: isOpen && token.colorPrimaryBorder,
          }}
        />
      </Popover>
      <section className="overflow-hidden max-w-100">
        <div className="d-flex gap-2">
          <Tag
            prefixCls="summary-filter"
            bordered={false}
            className="badge badge-light text-info w-100 d-inline-flex align-items-baseline"
          >
            <Typography.Text strong type="secondary" className="fs-6 mr-1">
              Ngày HĐ:
            </Typography.Text>
            <Tooltip
              title={`Từ ${clonedValues.startDate?.format(
                'L HH:mm',
              )} đến ${clonedValues.endDate?.format('L HH:mm')}`}
            >
              {
                _.find(SelectOptions.periodFilterOptions, {
                  value: clonedValues?.period,
                })?.label
              }
            </Tooltip>
          </Tag>
          <Tag
            prefixCls="summary-filter"
            bordered={false}
            className="badge badge-light text-info w-100 d-inline-flex align-items-baseline"
          >
            <Typography.Text strong type="secondary" className="fs-6 mr-1">
              NCC:
            </Typography.Text>
            {_.find(SelectOptions.partner, {
              value: filterParams.partnerKey,
            })?.label ?? ''}
          </Tag>
          {renderTag(
            'Trạm',
            'stationCode',
            _.find(stations, { value: clonedValues.stationCode })?.label ?? '',
            clonedValues.stationCode,
          )}
          {renderTag(
            'Cột bơm',
            'nozzleCode',
            _.find(nozzles, { value: clonedValues.nozzleCode })?.label ?? '',
            clonedValues.nozzleCode,
          )}
          {renderTag(
            'Thành tiền',
            ['totalAmountStart', 'totalAmountEnd'],
            `${
              clonedValues.totalAmountStart
                ? Utils.formatNumber(clonedValues.totalAmountStart)
                : 'Đầu'
            } đến ${
              clonedValues.totalAmountEnd
                ? Utils.formatNumber(clonedValues.totalAmountEnd)
                : 'Cuối'
            }`,
            [clonedValues.totalAmountStart, clonedValues.totalAmountEnd],
          )}
          {renderTag(
            'Trạng thái HĐ',
            'status',
            _.find(transactionSelect, { value: clonedValues.status })?.label ??
              '',
            clonedValues.status,
          )}
          {renderTag(
            'Hàng hóa',
            'productCode',
            _.find(goodsOptions, { value: clonedValues.productCode })?.label ??
              '',
            clonedValues.productCode,
          )}
        </div>
      </section>
    </div>
  )
}
